import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { IoClose } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { updateScholarship } from "../../redux/actions/scholarshipActions";
import { toast } from "react-hot-toast";

const extractFileName = (url) => {
  return url.substring(url.lastIndexOf("/") + 1, url.indexOf("?") === -1 ? url.length : url.indexOf("?"));
};

// Stored files are prefixed with a upload timestamp (e.g. "1782991003753-report.pdf");
// strip it so only the original document name is shown.
const getDisplayFileName = (fileName) => fileName.replace(/^\d+-/, "");

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ font: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["bold", "italic", "underline"],
    [{ align: [] }],
    ["clean"],
  ],
};

const formats = ["font", "header", "list", "bold", "italic", "underline", "align"];

export default function EditScholarshipPopup({ isOpen, onClose, scholarship, onSave }) {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image: null,
    imagePreview: null,
    existingImage: "",
    files: [],
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (scholarship) {
      setFormData({
        title: scholarship.title || "",
        description: scholarship.description || "",
        image: null,
        imagePreview: null,
        existingImage: (scholarship.image && scholarship.image[0]) || "",
        files: scholarship.files || [],
      });
    }
  }, [scholarship]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleQuillChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setFormData((prev) => ({ ...prev, image: file, imagePreview: reader.result }));
      reader.readAsDataURL(file);
    }
  };

  const handleFileRemove = (index) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const handleAddFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setFormData((prev) => ({ ...prev, files: [...prev.files, { file, existingPath: null }] }));
  };

  const stripHtml = useCallback((html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!formData.title?.trim()) {
        toast.error("Title is required.");
        return;
      }
      if (!stripHtml(formData.description).trim()) {
        toast.error("Description is required.");
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description);
      if (formData.image) formDataToSend.append("image", formData.image);

      const existingFiles = [];
      formData.files.forEach((fileData) => {
        if (fileData?.file) {
          formDataToSend.append("files", fileData.file);
        } else if (fileData) {
          existingFiles.push(extractFileName(fileData));
        }
      });
      formDataToSend.append("existing_files", JSON.stringify(existingFiles));

      try {
        await dispatch(updateScholarship(scholarship.id, formDataToSend));
        toast.success("Scholarship updated successfully!");
        onSave();
        onClose();
      } catch (error) {
        toast.error("Failed to update scholarship.");
      }
    },
    [dispatch, scholarship, formData, stripHtml, onSave, onClose]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 z-50 flex items-center justify-center w-full h-full bg-black bg-opacity-50">
      <div className="w-[1169px] p-4 bg-white rounded-lg shadow-lg max-h-[90vh]">
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            className="text-gray-500 border rounded-md shadow-md hover:text-gray-700"
            onClick={onClose}
          >
            <IoClose className="w-6 h-6" />
          </button>
        </div>
        <div className="overflow-y-auto max-h-[75vh] p-4">
          <h3 className="mb-4 text-xl font-semibold text-gray-700">Edit Scholarship</h3>
          <form onSubmit={handleSubmit}>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="title">
                Title
              </label>
              <input
                id="title"
                name="title"
                type="text"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
              />
            </div>

            <div className="mt-6">
              <label className="block mb-1 text-gray-700">Description</label>
              <ReactQuill
                value={formData.description}
                theme="snow"
                modules={modules}
                formats={formats}
                onChange={handleQuillChange}
                className="h-[150px] mb-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-4 mt-16">
              <div className="relative flex flex-col items-center justify-center w-full h-[225px] p-5 border border-gray-300 border-dashed rounded-md">
                {formData.imagePreview || formData.existingImage ? (
                  <img
                    src={formData.imagePreview || formData.existingImage}
                    alt="Preview"
                    className="absolute inset-0 object-cover w-full h-full opacity-50"
                  />
                ) : null}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  id="edit-scholarship-image-upload"
                />
                <label
                  htmlFor="edit-scholarship-image-upload"
                  className="px-4 py-2 mt-2 text-[#92288C] font-semibold bg-[#92288C] bg-opacity-10 rounded-md cursor-pointer z-10"
                >
                  Browse Image
                </label>
              </div>

              <div className="col-span-2">
                <label className="block mb-1 text-sm font-medium text-gray-600">Supporting Documents</label>
                <div className="space-y-4">
                  {formData.files.map((fileData, index) => {
                    const isNewFile = fileData && fileData.file;
                    const file = isNewFile ? fileData.file : fileData;
                    const fileURL = isNewFile ? URL.createObjectURL(file) : file;

                    return (
                      <div key={index} className="flex items-center space-x-4">
                        <span className="w-full px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md">
                          {typeof file === "string" ? getDisplayFileName(extractFileName(file)) : file.name}
                        </span>
                        <a
                          href={fileURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 text-white bg-[#92288C] rounded-md shadow hover:bg-[#6C0B67]"
                        >
                          View
                        </a>
                        <button
                          type="button"
                          className="px-4 py-2 text-white bg-[#92288C] rounded-md shadow hover:bg-[#6C0B67]"
                          onClick={() => handleFileRemove(index)}
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  onClick={handleAddFile}
                  className="mt-4 text-sm text-[#92288C] hover:underline"
                >
                  Add more
                </button>
              </div>
            </div>

            <div className="flex justify-end mt-6 space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-[#A8AAAE] bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-[#92288C] rounded-md hover:bg-[#6C0B67]"
              >
                Update
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
